// src/components/CariSecimModal.js
import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, TextInput, FlatList,
  TouchableOpacity, ActivityIndicator, Modal
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import CONFIG from '../config/Config';

function turkceNormalize(metin) {
  if (!metin) return '';
  return metin
    .replace(/ç/g, 'c').replace(/Ç/g, 'c')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'g')
    .replace(/ı/g, 'i').replace(/İ/g, 'i')
    .replace(/ö/g, 'o').replace(/Ö/g, 'o')
    .replace(/ş/g, 's').replace(/Ş/g, 's')
    .replace(/ü/g, 'u').replace(/Ü/g, 'u')
    .toLowerCase()
    .trim();
}

export default function CariSecimModal({ visible, onClose, onSelect, secilenCari }) {
  const [cariler, setCariler]       = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hata, setHata]             = useState('');

  useEffect(() => {
    if (visible) {
      setSearchText('');
      fetchCariler();
    }
  }, [visible]);

  const fetchCariler = async () => {
    setIsLoading(true);
    setHata('');
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/cari`, { headers: { 'X-App-Env': CONFIG.ENV } });
      const result = await response.json();
      if (result.data) {
        setCariler(result.data);
      } else {
        setHata('Cariler yüklenemedi.');
      }
    } catch {
      setHata('Sunucuya bağlanılamadı.');
    } finally {
      setIsLoading(false);
    }
  };

  const query = turkceNormalize(searchText);
  const filtrelenmis = cariler.filter(c =>
    !searchText ||
    turkceNormalize(c.Isim || '').includes(query) ||
    c.CariID?.toString().includes(searchText.trim())
  );

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <SafeAreaView style={styles.container} edges={['top', 'left', 'right', 'bottom']}>
          <View style={styles.inner}>

          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Ionicons name="close" size={22} color="#2c3e50" />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Cari Seç</Text>
            <View style={{ width: 36 }} />
          </View>

          {/* Arama */}
          <View style={styles.searchBox}>
            <Ionicons name="search-outline" size={17} color="#95a5a6" style={{ marginRight: 8 }} />
            <TextInput
              style={styles.searchInput}
              placeholder="Cari adı veya ID..."
              placeholderTextColor="#95a5a6"
              value={searchText}
              onChangeText={setSearchText}
              autoFocus
            />
            {searchText.length > 0 && (
              <TouchableOpacity onPress={() => setSearchText('')}>
                <Ionicons name="close-circle" size={17} color="#95a5a6" />
              </TouchableOpacity>
            )}
          </View>

          {/* Seçimi kaldır */}
          {secilenCari && (
            <TouchableOpacity
              style={styles.kaldir}
              onPress={() => { onSelect(null); onClose(); }}
            >
              <Ionicons name="person-remove-outline" size={15} color="#E53935" />
              <Text style={styles.kaldirText}>Cariye özel atamayı kaldır</Text>
            </TouchableOpacity>
          )}

          {/* İçerik */}
          {isLoading ? (
            <View style={styles.center}>
              <ActivityIndicator size="large" color="#FF9800" />
              <Text style={styles.infoText}>Cariler yükleniyor...</Text>
            </View>
          ) : hata ? (
            <View style={styles.center}>
              <Ionicons name="alert-circle-outline" size={40} color="#E53935" />
              <Text style={styles.infoText}>{hata}</Text>
              <TouchableOpacity style={styles.retryBtn} onPress={fetchCariler}>
                <Text style={styles.retryText}>Tekrar Dene</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <FlatList
              data={filtrelenmis}
              keyExtractor={item => item.CariID.toString()}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 30 }}
              renderItem={({ item }) => {
                const secili = secilenCari?.CariID === item.CariID;
                const cariHesapAcik = item.AcikHesap === 1;

                return (
                  <TouchableOpacity
                    style={[styles.cariItem, secili && styles.cariItemSecili]}
                    onPress={() => {
                      onSelect(item);
                      requestAnimationFrame(() => onClose());
                    }}
                  >
                    {/* Sol: ikon */}
                    <View style={[styles.cariIconBox, secili && styles.cariIconBoxSecili]}>
                      <Ionicons
                        name={secili ? "person" : "person-outline"}
                        size={18}
                        color={secili ? "#FF9800" : "#7f8c8d"}
                      />
                    </View>

                    {/* Orta: bilgiler */}
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.cariIsim, secili && styles.cariIsimSecili]} numberOfLines={1}>
                        {item.Isim}
                      </Text>

                      <View style={styles.altRow}>
                        <Text style={styles.cariIdText}>#{item.CariID}</Text>

                        {item.Indirim > 0 && (
                          <View style={styles.indirimBadge}>
                            <Text style={styles.indirimText}>%{item.Indirim} indirim</Text>
                          </View>
                        )}

                        {cariHesapAcik && (
                          <View style={styles.hesapBadge}>
                            <Text style={styles.hesapText}>Açık hesap</Text>
                          </View>
                        )}
                      </View>

                      {cariHesapAcik && item.CariBakiye !== 0 && (
                        <Text style={[
                          styles.bakiyeText,
                          item.CariBakiye > 0 ? styles.bakiyeArti : styles.bakiyeEksi
                        ]}>
                          Bakiye: {item.CariBakiye > 0 ? '+' : ''}{item.CariBakiye.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </Text>
                      )}
                    </View>

                    {secili && (
                      <Ionicons name="checkmark-circle" size={22} color="#FF9800" />
                    )}
                  </TouchableOpacity>
                );
              }}
              ListEmptyComponent={
                <View style={styles.center}>
                  <Ionicons name="people-outline" size={48} color="#bdc3c7" />
                  <Text style={styles.infoText}>Cari bulunamadı.</Text>
                </View>
              }
            />
          )}
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay:            { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  container:          { backgroundColor: '#F5F7FA', borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '90%', minHeight: '60%' },
  inner:              { flex: 1 },
  header:             { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#e9ecef' },
  closeBtn:           { width: 36, height: 36, borderRadius: 10, backgroundColor: '#f0f2f5', justifyContent: 'center', alignItems: 'center' },
  headerTitle:        { fontSize: 16, fontWeight: '700', color: '#2c3e50' },
  searchBox:          { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, margin: 16, marginBottom: 8, paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: '#e9ecef' },
  searchInput:        { flex: 1, fontSize: 15, color: '#2c3e50' },
  kaldir:             { flexDirection: 'row', alignItems: 'center', gap: 6, marginHorizontal: 16, marginBottom: 8, paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#FFEBEE', borderRadius: 8 },
  kaldirText:         { fontSize: 13, color: '#E53935', fontWeight: '600' },
  center:             { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 40, gap: 10 },
  infoText:           { fontSize: 14, color: '#95a5a6', textAlign: 'center' },
  retryBtn:           { backgroundColor: '#FF9800', paddingHorizontal: 20, paddingVertical: 8, borderRadius: 8 },
  retryText:          { fontSize: 14, color: '#fff', fontWeight: '600' },
  cariItem:           { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, marginBottom: 8, paddingHorizontal: 12, paddingVertical: 12, gap: 10, borderWidth: 1, borderColor: '#e9ecef' },
  cariItemSecili:     { borderColor: '#FF9800', backgroundColor: '#FFF8F0' },
  cariIconBox:        { width: 38, height: 38, borderRadius: 10, backgroundColor: '#f5f7fa', justifyContent: 'center', alignItems: 'center' },
  cariIconBoxSecili:  { backgroundColor: '#FFF3E0' },
  cariIsim:           { fontSize: 14, fontWeight: '700', color: '#2c3e50', marginBottom: 4 },
  cariIsimSecili:     { color: '#E65100' },
  altRow:             { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  cariIdText:         { fontSize: 11, color: '#95a5a6' },
  indirimBadge:       { backgroundColor: '#E8F5E9', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  indirimText:        { fontSize: 10, fontWeight: '700', color: '#2E7D32' },
  hesapBadge:         { backgroundColor: '#E3F2FD', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  hesapText:          { fontSize: 10, fontWeight: '700', color: '#1565C0' },
  bakiyeText:         { fontSize: 11, marginTop: 3 },
  bakiyeArti:         { color: '#2E7D32' },
  bakiyeEksi:         { color: '#C62828' },
});